import React, { useState, useEffect } from 'react';
import { message, Input, Button, Table, Modal } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { StyledCard } from '../common/StyledCard';
import { FormLabel } from '../common/FormLabel';
import { theme } from '../../theme';
import { EditOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

interface ITransportista {
  id: number;
  nc: string;
  denominacion: string;
}

interface ICamion {
  id: number;
  patente_camion: string;
  patente_acoplado: string;
}

const TransportistaCamionesAcoplados: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [transportistas, setTransportistas] = useState<ITransportista[]>([]);
  const [transportistaSeleccionado, setTransportistaSeleccionado] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [camiones, setCamiones] = useState<ICamion[]>([]);

  // Estados para la búsqueda de transportista
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [selectedTransportista, setSelectedTransportista] = useState<ITransportista | null>(null);

  // Estados para el modal
  const [modalVisible, setModalVisible] = useState(false);
  const [camionEditando, setCamionEditando] = useState<ICamion | null>(null);
  const [isGrabando, setIsGrabando] = useState(false);

  // Estados para los inputs del formulario
  const [patente_camion, setNumeroCamion] = useState('');
  const [patente_acoplado, setNumeroAcoplado] = useState('');

  // Obtener id_empresa del usuario
  const id_empresa = user?.id_empresa;

  // Efecto para cerrar el dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.transportista-search-container')) {
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cargar transportistas al iniciar
  useEffect(() => {
    const fetchTransportistas = async () => {
      if (!id_empresa) {
        message.error('No se pudo obtener el ID de empresa');
        return;
      }

      try {
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Error al cargar transportistas');
        }

        const data = await response.json();
        setTransportistas(data);
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar los transportistas');
      } finally {
        setIsLoading(false);
      }
    };

    if (id_empresa) {
      fetchTransportistas();
    }
  }, [getAccessTokenSilently, id_empresa]);

  const handleTransportistaChange = async (value: number) => {
    setTransportistaSeleccionado(value);
    setCamiones([]);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/camion/${id_empresa}/${value}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar camiones');
      }

      const data = await response.json();
      setCamiones(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los camiones');
    }
  };

  const abrirModalNuevo = () => {
    setCamionEditando(null);
    limpiarFormulario();
    setModalVisible(true);
  };

  const abrirModalEditar = (camion: ICamion) => {
    setCamionEditando(camion);
    setNumeroCamion(camion.patente_camion || '');
    setNumeroAcoplado(camion.patente_acoplado || '');
    setModalVisible(true);
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setCamionEditando(null);
    limpiarFormulario();
  };

  const limpiarFormulario = () => {
    setNumeroCamion('');
    setNumeroAcoplado('');
  };

  const validarFormulario = () => {
    return (
      patente_camion.trim() !== '' &&
      patente_acoplado.trim() !== ''
    );
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) {
      message.warning('Por favor complete todos los campos requeridos');
      return;
    }

    try {
      setIsGrabando(true);
      const token = await getAccessTokenSilently();

      const datosCamion = {
        patente_camion,
        patente_acoplado
      };

      let response;
      if (camionEditando) {
        // Modificar
        response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/editar_relacion_transportista_camion/${id_empresa}/${transportistaSeleccionado}/${camionEditando.id}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(datosCamion)
          }
        );
      } else {
        // Crear
        response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/crear_relacion_transportista_camion/${id_empresa}/${transportistaSeleccionado}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(datosCamion)
          }
        );
      }

      if (!response.ok) {
        throw new Error('Error al guardar los datos');
      }

      message.success(camionEditando ? 'Camión modificado correctamente' : 'Camión creado correctamente');
      cerrarModal();
      
      // Recargar lista de camiones
      if (transportistaSeleccionado) {
        handleTransportistaChange(transportistaSeleccionado);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al guardar los datos');
    } finally {
      setIsGrabando(false);
    }
  };

  // Estilos
  const sectionContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  };

  // Columnas de la tabla
  const columns: ColumnsType<ICamion> = [
    {
      title: 'Patente Camión',
      dataIndex: 'patente_camion',
      key: 'patente_camion',
      width: 200,
    },
    {
      title: 'Patente Acoplado',
      dataIndex: 'patente_acoplado',
      key: 'patente_acoplado',
      width: 200,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: ICamion) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => abrirModalEditar(record)}
          size="small"
        >
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px', fontSize: theme.fontSizes.normal }}>
          Cargando...
        </div>
      ) : (
        <StyledCard bodyStyle={{ padding: '12px' }}>
          <div style={sectionContainerStyle}>
            {/* Select Transportista con búsqueda */}
            <div style={{ flex: 1 }}>
              <FormLabel required>Transportista</FormLabel>
              <div className="transportista-search-container" style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Buscar por NC o denominación..."
                  required
                  style={{
                    width: '100%',
                    paddingLeft: '30px',
                    paddingRight: selectedTransportista ? '30px' : undefined,
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.sm,
                    marginBottom: '4px',
                    backgroundColor: selectedTransportista ? theme.colors.backgroundGray : 'white',
                    cursor: 'pointer',
                    height: '32px',
                    fontSize: '13px'
                  }}
                  value={selectedTransportista ? `${selectedTransportista.nc} - ${selectedTransportista.denominacion}` : searchTransportista}
                  onChange={e => {
                    setSearchTransportista(e.target.value);
                    setSelectedTransportista(null);
                    setTransportistaSeleccionado(null);
                    setShowSearchTransportista(true);
                  }}
                  onFocus={() => setShowSearchTransportista(true)}
                />
                {/* Icono de búsqueda */}
                <SearchOutlined style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.colors.textSecondary,
                  pointerEvents: 'none',
                  fontSize: '14px'
                }} />
                {/* Limpiar selección */}
                {selectedTransportista && (
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: theme.colors.textSecondary,
                      cursor: 'pointer',
                      fontSize: '14px',
                      zIndex: 1100
                    }}
                    onClick={() => {
                      setSelectedTransportista(null);
                      setSearchTransportista('');
                      setTransportistaSeleccionado(null);
                      setCamiones([]);
                      setShowSearchTransportista(true);
                    }}
                    title="Limpiar selección"
                  />
                )}
                {/* Dropdown */}
                {showSearchTransportista && transportistas.length > 0 && !selectedTransportista && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.sm,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: theme.shadows.md
                  }}>
                    {transportistas.filter(t => {
                      const search = searchTransportista.trim().toLowerCase();
                      return (
                        t.nc.toLowerCase().includes(search) ||
                        t.denominacion.toLowerCase().includes(search)
                      );
                    }).map(transportista => (
                      <div
                        key={transportista.id}
                        style={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.colors.borderLight}`,
                          textAlign: 'left',
                          fontSize: '13px'
                        }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = theme.colors.backgroundGray)}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                        onClick={() => {
                          setSelectedTransportista(transportista);
                          setTransportistaSeleccionado(transportista.id);
                          setShowSearchTransportista(false);
                          handleTransportistaChange(transportista.id);
                        }}
                      >
                        {transportista.nc} - {transportista.denominacion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Botón Nuevo Camión */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '4px' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={abrirModalNuevo}
                disabled={!transportistaSeleccionado}
                size="small"
                style={{ height: '32px', fontSize: '12px' }}
              >
                Nuevo Camión
              </Button>
            </div>
          </div>

          {/* Tabla de camiones */}
          {transportistaSeleccionado && (
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={camiones}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} camiones`,
              }}
              style={{ marginTop: '12px' }}
              locale={{
                emptyText: 'No hay camiones registrados para este transportista'
              }}
            />
          )}
        </StyledCard>
      )}

      {/* Modal para crear/editar camión */}
      <Modal
        title={camionEditando ? 'Editar Camión' : 'Nuevo Camión'}
        open={modalVisible}
        onCancel={cerrarModal}
        onOk={handleGuardar}
        confirmLoading={isGrabando}
        okText="Guardar"
        cancelText="Cancelar"
        width={500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '12px' }}>
          <div>
            <FormLabel required>Patente Camión</FormLabel>
            <Input
              value={patente_camion}
              onChange={(e) => setNumeroCamion(e.target.value)}
              placeholder="Ingrese patente del camión"
              style={{ height: '32px', fontSize: '13px' }}
              maxLength={15}
            />
          </div>

          <div>
            <FormLabel required>Patente Acoplado</FormLabel>
            <Input
              value={patente_acoplado}
              onChange={(e) => setNumeroAcoplado(e.target.value)}
              placeholder="Ingrese patente del acoplado"
              style={{ height: '32px', fontSize: '13px' }}
              maxLength={15}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TransportistaCamionesAcoplados;
